// ============= Imports =============

import { useEffect, useMemo, useState } from 'react';
import { createLogger } from '../../../../../utils/logger';
import type { SectionKey } from '../../../../../types';
import {
  FORM_SECTION_DEPENDENCY_FIELDS,
  SECTION_SCHEMA_REFRESH_FIELDS,
} from './constants';
import { usePrevious } from './usePrevious';
import { getDependencyValues } from './utils';

// ============= Constants =============

const logger = createLogger('useSectionSchemaRefresh');

// ============= Types =============

interface UseSectionSchemaRefreshParams {
  sectionKey: SectionKey;
  formData: Record<string, unknown> | undefined;
}

interface UseSectionSchemaRefreshReturn {
  schemaVersion: number;
}

// ============= Hook =============

export const useSectionSchemaRefresh = ({
  sectionKey,
  formData,
}: UseSectionSchemaRefreshParams): UseSectionSchemaRefreshReturn => {
  const [schemaVersion, setSchemaVersion] = useState(0);

  const refreshFields = useMemo(
    () => [
      ...FORM_SECTION_DEPENDENCY_FIELDS,
      ...(SECTION_SCHEMA_REFRESH_FIELDS[sectionKey] ?? []),
    ],
    [sectionKey]
  );

  const currentDependencyValues = useMemo(
    () => getDependencyValues(formData, refreshFields),
    [formData, refreshFields]
  );

  const previousDependencyValues = usePrevious(currentDependencyValues);

  useEffect(() => {
    if (previousDependencyValues === undefined) return;

    const changedFields = refreshFields.filter(
      (field) =>
        previousDependencyValues[field] !== currentDependencyValues[field]
    );

    if (changedFields.length === 0) return;

    setSchemaVersion((prev) => prev + 1);
    logger.debug('Dependency change detected, refreshing schema', {
      section: sectionKey,
      changedFields,
    });
  }, [
    currentDependencyValues,
    previousDependencyValues,
    refreshFields,
    sectionKey,
  ]);

  return { schemaVersion };
};
